import { ExpandCircleDownIcon, Tag } from '@mercai/clever'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { SupplyOrderStatusEnum } from '@/types'
import { useTranslation } from 'react-i18next'
import { SupplyOrderStatusSelectProps } from './supply-order-status-select.type'

import { ModalSupplyOrderCancellationReason } from '../../../../components'

import styles from './supply-order-status-select.module.css'

export const SupplyOrderStatusSelect = ({
  values,
  setValues,
  initialStatus,
}: SupplyOrderStatusSelectProps) => {
  const { t: tEnums } = useTranslation('enums')

  const [isOpen, setIsOpen] = useState(false)
  const [isOpenCancellationModal, setIsOpenCancellationModal] = useState(false)

  const buttonRef = useRef<HTMLDivElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)

  const statuses: Record<SupplyOrderStatusEnum, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('supply_order_status_pending')}
        <ExpandCircleDownIcon style={{ fill: 'var(--warning-800)' }} />
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light">
        {tEnums('supply_order_status_completed')}
        <ExpandCircleDownIcon style={{ fill: 'var(--success-800)' }} />
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('supply_order_status_cancelled')}
        <ExpandCircleDownIcon style={{ fill: 'var(--danger-800)' }} />
      </Tag>
    ),
  }

  const togglePopper = () => {
    setIsOpen((prev) => !prev)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popperRef.current &&
      !popperRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  const onChangeStatus = (status: keyof typeof SupplyOrderStatusEnum) => {
    if (status !== 'cancelled') {
      setIsOpen(false)
      return setValues((prev) => ({
        ...prev,
        status,
        cancellationDescription: undefined,
        cancellationReason: undefined,
        action: undefined,
      }))
    }

    setIsOpenCancellationModal(true)
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className={styles.core}>
      <div
        ref={buttonRef}
        onClick={() => (initialStatus !== 'cancelled' ? togglePopper() : null)}
        className={styles['status-button']}
      >
        {statuses[values?.status || 'pending']}
      </div>
      {isOpen && (
        <div ref={popperRef} className={styles.popper}>
          <ul>
            {Object.keys(SupplyOrderStatusEnum).map((key) => (
              <li
                key={key}
                className={key === values?.status ? styles.active : ''}
                onClick={() =>
                  initialStatus !== 'cancelled'
                    ? onChangeStatus(key as keyof typeof SupplyOrderStatusEnum)
                    : null
                }
              >
                {tEnums(`supply_order_status_${key}`)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ModalSupplyOrderCancellationReason
        isOpen={isOpenCancellationModal}
        setIsOpen={setIsOpenCancellationModal}
        onConfirm={({ action, cancellationDescription, cancellationReason }) =>
          setValues((prev) => ({
            ...prev,
            status: 'cancelled',
            cancellationDescription,
            cancellationReason,
            action,
          }))
        }
      />
    </div>
  )
}
